import { ccm19GetAcceptedEmbeddings } from "src/integrations/ccm19/api"
import { Ccm19EmbeddingName } from "src/integrations/ccm19/embeddings"
import { PlainUserData } from "src/integrations/lib/mapping"
import { metaInit } from "src/integrations/meta/events"
import { cleanObject } from "src/utilities/object"
import { PartialDeep } from "type-fest"

// https://support.google.com/google-ads/answer/13258081
// https://developers.google.com/tag-platform/tag-manager/server-side/common-event-data

/**
 * Represents user data with various parameters used for identification and targeting.
 * @see https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching/#reference
 */
export type MetaUserData = {
  /**
   * Email address in unhashed lowercase or SHA-256 hashed format.
   * Example: "jsmith@example.com" or "6e3913852f512d76acff15d1e402c7502a5bbe6101745a7120a2a4833ebd2350"
   */
  em: string

  /**
   * First name in lowercase letters.
   * Example: "john"
   */
  fn: string

  /**
   * Last name in lowercase letters.
   * Example: "smith"
   */
  ln: string

  /**
   * Phone number including country code and area code, digits only.
   * Example: "16505554444"
   */
  ph: string

  /**
   * Unique external identifier from the advertiser (e.g., loyalty membership ID, user ID, external cookie ID).
   * Example: "a@example.com"
   */
  external_id: string

  /**
   * Gender as a single lowercase letter ("f" for female, "m" for male), or left blank if unknown.
   * Example: "f"
   */
  ge?: "f" | "m"

  /**
   * Birthdate in digits only format (YYYYMMDD).
   * Example: "19910526" (for May 26, 1991)
   */
  db: string

  /**
   * City name in lowercase with spaces removed.
   * Example: "menlopark"
   */
  ct: string

  /**
   * Two-letter state or province code in lowercase.
   * Example: "ca"
   */
  st: string

  /**
   * Zip or postal code as a string.
   * Example: "94025"
   */
  zp: string

  /**
   * Two-letter country code in lowercase.
   * Example: "us"
   */
  country: string
}

export function mapToMetaUserData(plainUser: PartialDeep<PlainUserData> | undefined): Partial<MetaUserData> {
  return cleanObject<Partial<MetaUserData>>({
    em: plainUser?.email?.toLowerCase(),
    fn: plainUser?.address?.firstName?.toLowerCase(),
    ln: plainUser?.address?.lastName?.toLowerCase(),
    ph: plainUser?.phoneNormalized?.replace(/\D/g, ""),
    external_id: plainUser?.userId,
    ge: undefined,
    db: undefined,
    country: plainUser?.address?.countryCode?.toLowerCase(),
    st: plainUser?.address?.regionCode?.toLowerCase(),
    zp: plainUser?.address?.postalCode,
    ct: plainUser?.address?.city?.replace(/\s+/g, "").toLowerCase(),
  })
}

export function anonymizeMetaUserData(userData: Partial<MetaUserData>) {
  return cleanObject<Partial<MetaUserData>>({
    country: userData?.country,
    st: userData?.st,
    zp: userData?.zp,
    ct: userData?.ct,
  })
}

let previousUserData: Partial<MetaUserData> | undefined

export function metaUpdateUserData(userData: PartialDeep<PlainUserData> | undefined) {
  const metaUserDataFull = mapToMetaUserData(userData)
  const consented = ccm19GetAcceptedEmbeddings().some((embedding) => embedding.name === Ccm19EmbeddingName.MetaPixel)
  const metaUserData = consented ? metaUserDataFull : anonymizeMetaUserData(metaUserDataFull)

  if (
    previousUserData &&
    metaUserData.em === previousUserData?.em &&
    metaUserData.fn === previousUserData?.fn &&
    metaUserData.ln === previousUserData?.ln &&
    metaUserData.ph === previousUserData?.ph &&
    metaUserData.external_id === previousUserData?.external_id &&
    metaUserData.ge === previousUserData?.ge &&
    metaUserData.db === previousUserData?.db &&
    metaUserData.ct === previousUserData?.ct &&
    metaUserData.st === previousUserData?.st &&
    metaUserData.zp === previousUserData?.zp &&
    metaUserData.country === previousUserData?.country &&
    true
  ) {
    return
  }

  metaInit(metaUserData)
  previousUserData = metaUserData
}
